import { TFunction } from "i18next"
import { SchoolGroupSubscriptionFragment } from "../api/generated"
import getStripePaymentPortalHref from "../stripe/utils/getStripePaymentPortalHref"
import { getIsSubscriptionUsable } from "../subscription/utils"
import {
  MENTOR_GROUP_MEMBERSHIP_ROLE_ADMIN,
  MENTOR_GROUP_MEMBERSHIP_ROLE_MEMBER,
  MENTOR_GROUP_MEMBERSHIP_ROLE_MODERATOR
} from "./constants"

export const getCommunityGroupRoleLabel = (
  role: number,
  translator: TFunction
) => {
  switch (role) {
    case MENTOR_GROUP_MEMBERSHIP_ROLE_MEMBER:
      return translator("member")
    case MENTOR_GROUP_MEMBERSHIP_ROLE_MODERATOR:
      return translator("mentor")
    case MENTOR_GROUP_MEMBERSHIP_ROLE_ADMIN:
      return translator("group admin")
  }
}

// including groupId1-5 capture group names for visual clarity
const basePathnameRe = new RegExp(
  "/community/groups/(?:(?<groupId1>\\d+)/)(?:(?<groupId2>\\d+)/)?(?:(?<groupId3>\\d+)/)?(?:(?<groupId4>\\d+)/)?(?:(?<groupId5>\\d+)/)?"
)

// util for getting the base pathname for mentor groups routing
const getBaseToGroup = (groupId: number) => {
  if (groupId) {
    const match = window.location.pathname.match(basePathnameRe)

    // find if current pathname includes provided groupId
    if (match) {
      const groupIds = match.slice(1).filter(Boolean)
      const lastGroupId = parseInt(groupIds[groupIds.length - 1])

      // ensure we aren't appending groupId when groupId already exists in current pathname
      return groupId === lastGroupId ? match[0] : `${match[0]}${groupId}/`
    }

    // no match found, use provided groupId
    return `/community/groups/${groupId}/`
  }

  // no groupId provided, use base route
  return `/community/groups/`
}

// mentor group to prop utils
export const getToGroup = (groupId: number) => getBaseToGroup(groupId)
export const getToGroupAdd = (groupId: number) =>
  `${getBaseToGroup(groupId)}add-group/`
export const getToGroupAddMembers = (groupId: number) => `${getBaseToGroup(groupId)}add-member/` // prettier-ignore
export const getToGroupManage = (groupId: number) =>
  `${getBaseToGroup(groupId)}manage/`
export const getToGroupManageMember = (groupId: number, memberId: number) =>
  `${getBaseToGroup(groupId)}manage-member-${memberId}/`

// Get the appropriate link props for the group subscription
// based on whether the subscription is usable.
export function getGroupSubscriptionLinkProps(
  groupSubscription: SchoolGroupSubscriptionFragment
) {
  const isSubscriptionUsable = getIsSubscriptionUsable(
    groupSubscription?.subscription
  )
  // Attempt to determine if the user can manage the subscription.
  // May be poor UX to route user to /subscribe flow when we cannot determine
  // that they can manage the subscription (but same could be said of the opposite, to
  // route them to the Stripe payment portal when they cannot manage the subscription),
  // but currently all use cases of getGroupSubscriptionLinkProps are successfully
  // passing in the groupSubscription, so we should be correctly determining this.
  const canManage =
    !!groupSubscription?.community_group?.permissions?.can_edit_subscription

  return isSubscriptionUsable && canManage
    ? {
        href: getStripePaymentPortalHref(),
        target: "_blank"
      }
    : {
        to: "/subscribe/"
      }
}
